import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileText, Check, Trash2, AlertTriangle } from "lucide-react";

export const ReceiptFields = ({ form, setForm, categories, idPrefix }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className="col-span-2 sm:col-span-1">
      <Label className="text-xs">Vendor</Label>
      <Input value={form.vendor || ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
        data-testid={`${idPrefix}-vendor`} className="mt-1 rounded-xl" placeholder="Store or supplier" />
    </div>
    <div className="col-span-2 sm:col-span-1">
      <Label className="text-xs">Date</Label>
      <Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
        data-testid={`${idPrefix}-date`} className="mt-1 rounded-xl" />
    </div>
    <div>
      <Label className="text-xs">Total amount</Label>
      <Input type="number" step="0.01" min="0" value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: e.target.value })}
        data-testid={`${idPrefix}-amount`} className="mt-1 rounded-xl" placeholder="0.00" />
    </div>
    <div>
      <Label className="text-xs">Category</Label>
      <Select value={form.category || "Other"} onValueChange={(v) => setForm({ ...form, category: v })}>
        <SelectTrigger className="mt-1 rounded-xl" data-testid={`${idPrefix}-category`}><SelectValue /></SelectTrigger>
        <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="col-span-2">
      <Label className="text-xs">Notes / items</Label>
      <Input value={form.items || ""} onChange={(e) => setForm({ ...form, items: e.target.value })}
        data-testid={`${idPrefix}-items`} className="mt-1 rounded-xl" placeholder="What was this for?" />
    </div>
  </div>
);

export const ReceiptReviewCard = ({ item, categories, onDone }) => {
  const r = item.receipt;
  const [form, setForm] = useState(r ? { vendor: r.vendor, date: r.date || "", amount: r.amount, category: r.category, items: r.items } : {});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.date) { toast.error("Add the receipt date"); return; }
    setBusy(true);
    try {
      await api.put(`/vault/receipts/${r.receipt_id}`, { ...form, amount: parseFloat(form.amount || 0) });
      toast.success("Receipt saved to your vault");
      onDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const discard = async () => {
    setBusy(true);
    try {
      if (r) await api.delete(`/vault/receipts/${r.receipt_id}`);
      onDone(false);
    } catch { toast.error("Could not discard"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-4 flex flex-col sm:flex-row gap-4" data-testid="receipt-review-card">
      <div className="sm:w-28 shrink-0">
        {item.preview ? (
          <img src={item.preview} alt="receipt" className="w-full sm:w-28 h-36 object-cover rounded-xl border border-[#E5E5E5]" />
        ) : (
          <div className="w-full sm:w-28 h-36 rounded-xl bg-[#F9F9F8] border border-[#E5E5E5] flex items-center justify-center"><FileText className="h-8 w-8 text-[#114A31]" /></div>
        )}
        <p className="mt-1 text-[10px] text-[#595D5A] truncate">{item.name}</p>
      </div>
      <div className="flex-1 min-w-0">
        {item.status === "reading" && (
          <p className="text-sm text-[#595D5A] flex items-center gap-2 py-6"><Loader2 className="h-4 w-4 animate-spin text-[#114A31]" /> Uploading & reading with AI…</p>
        )}
        {item.status === "error" && (
          <div className="py-4">
            <p className="text-sm text-[#991B1B] flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {item.error}</p>
            <Button onClick={() => onDone(false)} variant="outline" size="sm" className="mt-3 rounded-full">Dismiss</Button>
          </div>
        )}
        {item.status === "review" && (
          <>
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-sm font-semibold text-[#1C1C1C]">Check the details, then save</p>
              {r.confidence < 0.6 && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92600A]" data-testid="receipt-low-confidence">Please double-check</span>
              )}
            </div>
            {r.is_receipt === false && (
              <p className="text-xs text-[#92600A] mb-2">This doesn't look like a receipt — you can still save it as an expense or discard it.</p>
            )}
            <ReceiptFields form={form} setForm={setForm} categories={categories} idPrefix="review" />
            <div className="mt-3 flex gap-2">
              <Button onClick={save} disabled={busy} data-testid="receipt-save-btn" className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white flex-1 sm:flex-none">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Save receipt</>}
              </Button>
              <Button onClick={discard} disabled={busy} variant="ghost" data-testid="receipt-discard-btn" className="rounded-full text-[#991B1B] hover:bg-[#FEE2E2]">
                <Trash2 className="h-4 w-4 mr-1.5" /> Discard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReceiptReviewCard;
